import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth, SignInModal } from "./auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = false;

function PayoutInfoCard({ aff, onSaved }) {
  const existing = aff?.payout_method;
  const summary = aff?.payout_details_summary || {};
  const [editing, setEditing] = useState(!existing);
  const [method, setMethod] = useState(existing || "cashapp");
  const [cashtag, setCashtag] = useState("");
  const [venmo, setVenmo] = useState("");
  const [paypal, setPaypal] = useState("");
  const [zelle, setZelle] = useState("");
  const [bankName, setBankName] = useState("");
  const [routing, setRouting] = useState("");
  const [account, setAccount] = useState("");
  const [acctType, setAcctType] = useState("checking");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr(""); setBusy(true);
    const body = { method };
    if (method === "cashapp") body.cashtag = cashtag;
    else if (method === "venmo") body.venmo_username = venmo;
    else if (method === "paypal") body.paypal_email = paypal;
    else if (method === "zelle") body.zelle_handle = zelle;
    else if (method === "bank") {
      body.bank_holder_name = bankName;
      body.bank_routing_number = routing;
      body.bank_account_number = account;
      body.bank_account_type = acctType;
    }
    try {
      const r = await axios.put(`${API}/affiliate/payout-info`, body);
      onSaved?.(r.data);
      setEditing(false);
      setCashtag(""); setVenmo(""); setPaypal(""); setZelle(""); setBankName(""); setRouting(""); setAccount("");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed to save");
    }
    setBusy(false);
  };

  const summaryLine = (() => {
    if (!existing) return null;
    if (existing === "cashapp") return summary.cashtag;
    if (existing === "venmo") return summary.username;
    if (existing === "paypal") return summary.email;
    if (existing === "zelle") return summary.handle;
    if (existing === "bank") return `${summary.holder_name || ""} · ${summary.account_type || ""} ···· ${summary.account_last4 || ""}`;
    return null;
  })();

  return (
    <div data-testid="payout-info-card" className="mt-8 border border-gold/40 rounded-sm p-6 bg-gradient-to-br from-gold/10 to-transparent">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-serif-accent text-gold text-sm">~ Payouts</div>
          <div className="font-display uppercase text-lg tracking-tight mt-0.5">Where should we send your money?</div>
          {existing && !editing && (
            <div className="text-muted text-xs mt-2">
              Currently set to <span className="text-gold uppercase tracking-[0.2em] text-[10px] font-display mr-1">{existing}</span>
              <span className="text-white font-mono" data-testid="payout-summary">{summaryLine}</span>
            </div>
          )}
          {!existing && (
            <div className="text-muted text-xs mt-2 leading-relaxed">Add your payout destination now — when admin releases funds they'll send straight to your Cash App, Venmo, PayPal, Zelle, or bank.</div>
          )}
        </div>
        {existing && !editing && (
          <button data-testid="edit-payout-btn" onClick={() => setEditing(true)} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 border border-white/20 hover:border-gold/50 hover:text-gold rounded-sm">Change</button>
        )}
      </div>

      {editing && (
        <div className="mt-5 space-y-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {[
              { id: "cashapp", label: "Cash App" },
              { id: "venmo", label: "Venmo" },
              { id: "paypal", label: "PayPal" },
              { id: "zelle", label: "Zelle" },
              { id: "bank", label: "Bank (ACH)" },
            ].map(m => (
              <button key={m.id} data-testid={`payout-method-${m.id}`} onClick={() => setMethod(m.id)} className={`font-display uppercase text-[10px] tracking-[0.2em] px-3.5 py-2 rounded-sm border whitespace-nowrap ${method === m.id ? "border-gold bg-gold text-bg" : "border-white/20 text-muted hover:text-white"}`}>{m.label}</button>
            ))}
          </div>

          {method === "cashapp" && (
            <input data-testid="cashapp-input" value={cashtag} onChange={(e) => setCashtag(e.target.value)} placeholder="$cashtag" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
          )}
          {method === "venmo" && (
            <input data-testid="venmo-input" value={venmo} onChange={(e) => setVenmo(e.target.value)} placeholder="@venmo-username" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
          )}
          {method === "paypal" && (
            <input data-testid="paypal-input" type="email" value={paypal} onChange={(e) => setPaypal(e.target.value)} placeholder="you@paypal.com" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
          )}
          {method === "zelle" && (
            <input data-testid="zelle-input" value={zelle} onChange={(e) => setZelle(e.target.value)} placeholder="Email or phone linked to Zelle" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
          )}
          {method === "bank" && (
            <div className="space-y-3">
              <input data-testid="bank-name-input" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="Account holder name" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="routing-input" value={routing} onChange={(e) => setRouting(e.target.value.replace(/[^0-9]/g, "").slice(0, 9))} placeholder="Routing # (9 digits)" inputMode="numeric" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                <input data-testid="account-input" value={account} onChange={(e) => setAccount(e.target.value.replace(/[^0-9]/g, "").slice(0, 17))} placeholder="Account #" inputMode="numeric" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
              </div>
              <div className="flex gap-2">
                {["checking", "savings"].map(t => (
                  <button key={t} data-testid={`acct-type-${t}`} onClick={() => setAcctType(t)} className={`flex-1 font-display uppercase text-[10px] tracking-[0.2em] py-2.5 rounded-sm border ${acctType === t ? "border-gold bg-gold text-bg" : "border-white/20 text-muted"}`}>{t}</button>
                ))}
              </div>
            </div>
          )}

          {err && <div data-testid="payout-err" className="text-red-400 text-xs">{err}</div>}

          <div className="flex gap-2">
            <button data-testid="payout-save-btn" onClick={save} disabled={busy} className="flex-1 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">
              {busy ? "Saving…" : existing ? "Update payout info" : "Save payout info"}
            </button>
            {existing && (
              <button onClick={() => { setEditing(false); setErr(""); }} className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-3 border border-white/20 hover:text-white rounded-sm">Cancel</button>
            )}
          </div>

          <div className="text-[10px] text-muted/70 leading-relaxed font-serif-accent">
            We store this securely and only release to admin at payout time. You can change your method anytime.
          </div>
        </div>
      )}
    </div>
  );
}

function SimpleNav({ user, onLogout }) {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-2xl bg-bg/75 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
        <Link to="/" data-testid="logo-home-btn" className="flex items-center gap-2.5 group">
          <div className="relative w-9 h-9 flex items-center justify-center">
            <div className="absolute inset-0 bg-gold-grad rounded-sm rotate-45 group-hover:rotate-[54deg] transition-transform duration-500" />
            <span className="relative font-display text-bg text-xl">Z</span>
          </div>
          <div className="leading-none">
            <div className="font-display text-lg tracking-tight">ZIP CITY</div>
            <div className="font-serif-accent text-gold text-[11px] -mt-0.5">Las Vegas</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          {user && <button data-testid="logout-btn" onClick={onLogout} className="font-display uppercase text-[10px] tracking-[0.2em] text-muted hover:text-white px-3 py-2">Sign out</button>}
          <Link to="/" className="font-display uppercase text-[11px] tracking-[0.22em] px-4 py-2.5 border border-white/20 hover:border-gold/40 hover:text-gold rounded-sm">Back to City</Link>
        </div>
      </div>
    </header>
  );
}

export default function AffiliatePage() {
  const { user, logout, loading } = useAuth();
  const [aff, setAff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payouts, setPayouts] = useState(null);
  const [signInOpen, setSignInOpen] = useState(false);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/affiliate/me`).then(r => setAff(r.data)).catch(() => {});
      axios.get(`${API}/affiliate/me/payouts`).then(r => setPayouts(r.data)).catch(() => {});
    }
  }, [user]);

  const signUp = async () => {
    if (!user) { setSignInOpen(true); return; }
    setBusy(true);
    try {
      const r = await axios.get(`${API}/affiliate/me`);
      setAff(r.data);
    } catch {}
    setBusy(false);
  };

  const signed = !!aff;

  const link = aff ? `${window.location.origin}/?ref=${aff.code}` : "";

  const copy = () => {
    navigator.clipboard.writeText(link);
    alert("Link copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-bg text-white grain">
      <SimpleNav user={user} onLogout={logout} />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1519638831568-d9897f54ed69?w=2400')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-bg via-bg/50 to-bg" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
          <div className="font-serif-accent text-gold text-lg mb-4">~ The Zip City Influencer Program ~</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9]">
            Promote Vegas. <br /><span className="gold-text">Earn Forever.</span>
          </h1>
          <p className="mt-6 text-muted max-w-2xl mx-auto leading-relaxed">
            Sign up any Vegas business and collect <span className="text-gold">20% of their subscription</span> every month for the life of their account. No quotas. No clawbacks. No expiry.
          </p>
          {!signed ? (
            <button data-testid="affiliate-signup-btn" disabled={busy || loading} onClick={signUp} className="mt-10 font-display uppercase tracking-[0.22em] text-sm py-4 px-10 bg-gold-grad text-bg rounded-sm hover:brightness-110 active:scale-[0.98] shadow-gold transition-all disabled:opacity-60">
              {user ? (busy ? "Generating…" : "Activate My Affiliate Code") : "Sign Up · Get Your QR"}
            </button>
          ) : (
            <div data-testid="affiliate-signed-in" className="mt-8 inline-block border border-gold/30 rounded-sm px-6 py-3 font-serif-accent text-gold">You're in. Scroll down for your referral kit.</div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="font-serif-accent text-gold text-center mb-3">How it works</div>
        <h2 className="font-display uppercase text-center text-3xl lg:text-4xl tracking-tight mb-14">Three steps. Lifetime passive income.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Claim Your Code", d: "Hit Sign Up and we mint a unique QR + referral link tied to your wallet." },
            { n: "02", t: "Share Anywhere", d: "Drop it in your bio, on flyers at the Strip, in your Instagram story, or on business cards you slide to bartenders." },
            { n: "03", t: "Get Paid Monthly", d: "Every business that signs up earns you 20% of their $29–$99 plan — every single month they stay subscribed." },
          ].map((s) => (
            <div key={s.n} className="border border-white/10 rounded-sm p-7 bg-surface/40 hover:border-gold/30 transition-all">
              <div className="font-serif-accent gold-text text-5xl">{s.n}</div>
              <div className="font-display uppercase text-xl tracking-tight mt-3">{s.t}</div>
              <div className="text-muted text-sm mt-2 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROMO CALLOUT */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="relative overflow-hidden border border-gold/30 rounded-sm bg-gradient-to-br from-gold/8 via-transparent to-gold/5 p-8 lg:p-12">
          <div className="font-serif-accent text-gold text-sm uppercase tracking-[0.22em]">Referral bonus</div>
          <div className="font-display text-3xl lg:text-5xl uppercase tracking-tight mt-2">
            Sign-ups through your code get the <span className="gold-text">first month free</span>.
          </div>
          <p className="text-muted mt-4 max-w-2xl">Zip City automatically extends the 14-day trial to a full <strong className="text-gold">30 days</strong> when a business subscribes via your QR. Sweeter offer = more conversions. Still 20% yours.</p>
        </div>
      </section>

      {/* DASHBOARD */}
      {signed && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="font-serif-accent text-gold text-center mb-3">Your referral kit</div>
          <h2 className="font-display uppercase text-center text-3xl lg:text-4xl tracking-tight mb-10">Code · QR · Earnings</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-white/10 rounded-sm p-8 bg-surface/40">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted mb-2 font-display">Your unique code</div>
              <div data-testid="affiliate-code" className="font-display text-4xl gold-text tracking-tight">{aff?.code || "…"}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted mt-5 mb-2 font-display">Referral link</div>
              <div data-testid="affiliate-link" className="font-mono text-sm text-gold break-all">{link || "…"}</div>
              <button data-testid="copy-affiliate-btn" onClick={copy} className="w-full mt-5 font-display uppercase tracking-[0.2em] text-xs py-3 border border-white/20 hover:bg-white hover:text-bg rounded-sm transition-all">Copy Link</button>
            </div>
            <div className="border border-white/10 rounded-sm p-8 bg-surface/40 flex flex-col items-center justify-center">
              <div data-testid="affiliate-qr" className="bg-white p-4 rounded-sm">
                {link && <QRCodeCanvas value={link} size={180} level="M" />}
              </div>
              <div className="text-muted text-xs mt-3 font-serif-accent">Scan · screenshot · print</div>
            </div>
          </div>
          {aff && (
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="border border-gold/20 rounded-sm p-6 text-center bg-surface/40">
                <div data-testid="earned-total" className="font-display text-4xl gold-text">${(aff.total_earned_cents / 100).toFixed(2)}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display mt-1">Earned to date</div>
              </div>
              <div className="border border-gold/20 rounded-sm p-6 text-center bg-surface/40">
                <div data-testid="paid-out-total" className="font-display text-4xl text-white/80">${((payouts?.paid_out_cents || 0) / 100).toFixed(2)}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display mt-1">Paid out</div>
              </div>
              <div className="border border-gold/40 rounded-sm p-6 text-center bg-gradient-to-br from-gold/10 to-transparent">
                <div data-testid="balance-pending" className="font-display text-4xl gold-text">${((payouts?.balance_cents || aff.total_earned_cents) / 100).toFixed(2)}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display mt-1">Balance owed</div>
              </div>
            </div>
          )}
          {payouts?.payouts?.length > 0 && (
            <div data-testid="payout-history" className="mt-8 border border-white/10 rounded-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-white/5 bg-bg/50 font-display uppercase text-[11px] tracking-[0.22em] text-muted">Payout history</div>
              {payouts.payouts.map(p => (
                <div key={p.id} className="px-5 py-3 border-b border-white/5 last:border-0 flex items-center justify-between">
                  <div>
                    <div className="font-display gold-text">${(p.amount_cents / 100).toFixed(2)}</div>
                    <div className="text-muted text-xs font-serif-accent">{p.method}{p.note ? ` · ${p.note}` : ""}</div>
                  </div>
                  <div className="text-muted text-xs font-mono">{new Date(p.paid_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}

          <PayoutInfoCard aff={aff} onSaved={(data) => setAff({ ...aff, payout_method: data.payout_method, payout_details_summary: data.payout_details_summary, payout_info_updated_at: data.payout_info_updated_at })} />
        </section>
      )}

      <footer className="border-t border-white/5 py-10 text-center text-muted text-sm">
        <div className="font-serif-accent text-gold text-lg">Zip City</div>
        <div className="mt-1">© {new Date().getFullYear()} — A neon love letter to Las Vegas.</div>
      </footer>

      <SignInModal
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        message="Quick sign-in to activate your affiliate code — 20% recurring on every referred subscription."
      />
    </div>
  );
}
